import { firstValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Orientation, OrientationConfiguration } from 'ngx-guided-tour';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { endpoints } from '../core/http/api.endpoints';
import { HttpRequestService } from '../core/http/http-request.service';
import { StorageService } from '../core/storage/storage.service';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  popUpMessage = new Subject<any>();
  data = new Subject<any>();
  deleted = new Subject<any>();
  fileType = new BehaviorSubject<string>('');
  // fileType = new Subject<any>();
  filterContext = new BehaviorSubject<string>('space-frontier'); // 'space-frontier' or 'galaxy'
  subscriptionStatus = new Subject<any>();
  sort = new Subject<any>();
  sortForBrowsing: boolean;
  fileOptionsData: any;
  galaxyForAction: any;
  galaxyCreated = new Subject<any>();
  refresh = new Subject<any>();
  download = new Subject<any>();
  upload = new Subject<any>();
  uploadfileName = new Subject<any>();
  accountType = new Subject<any>();
  isStar: boolean;
  warningPopUpTitle = new Subject<string>();
  warningPopUpMessage = new Subject<string>();
  spaceJammitTabs = new BehaviorSubject<string>('');
  warningPopUpShowKikiMessage = new Subject<boolean>();
  private shareData = new BehaviorSubject<any>(null);
  private readonly messageKey = 'firstVisitMessageShown';
  private galaxyData!: BehaviorSubject<any>;
  data$!: Observable<any>;
  private readonly STORAGE_KEY = 'app_status';
  private statusSubject!: BehaviorSubject<boolean>;
  status$!: Observable<boolean>;
  private pGalaxyData!: BehaviorSubject<any>;
  pData$!: Observable<any>;

  constructor(
    private spinner: NgxSpinnerService,
    private http: HttpRequestService,
    private toastrService: ToastrService,
    private storage: StorageService
  ) {
    this.galaxyData = new BehaviorSubject<any>(this.loadFromLocal());
    this.statusSubject = new BehaviorSubject<boolean>(this.getStoredValue());
    this.pGalaxyData = new BehaviorSubject<any>(this.pGalaxyloadFromLocal());

    this.data$ = this.galaxyData.asObservable();
    this.status$ = this.statusSubject.asObservable();
    this.pData$ = this.pGalaxyData.asObservable();
  }

  private isBrowser(): boolean {
    return typeof window !== 'undefined' && !!window.localStorage;
  }

  showMessageOnFirstVisit(): boolean {
    if (!this.isBrowser() || typeof sessionStorage === 'undefined') {
      return false;
    }

    const messageShown = sessionStorage.getItem(this.messageKey);
    if (!messageShown) {
      sessionStorage.setItem(this.messageKey, 'true');
      return true;
    }
    return false;
  }

  presentSpinner(name?: string) {
    this.spinner.show(name);
  }

  dismissSpinner(name?: string) {
    this.spinner.hide(name);
  }

  setNewInfo(user: any) {
    this.shareData.next(user);
  }
  getNewInfo() {
    return this.shareData.asObservable();
  }
  getHowToVideos() {
    return new Promise((resolve, reject) => {
      this.http.get(endpoints.system_Content.howToVideos).subscribe(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  getSystemContentAndArchives() {
    return new Promise((resolve, reject) => {
      this.http.get(endpoints.system_Content.contentAndArchiveImages).subscribe(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }
  showWarningToastr(message: string, title?: string) {
    this.toastrService.warning(message, title, {
      positionClass: 'toast-bottom-center',
    });
  }
  showErrorToastr(message: string, title?: string) {
    this.toastrService.error(message, title, {
      positionClass: 'toast-bottom-center',
    });
  }
  showSuccessToastr(message: string, title?: string) {
    this.toastrService.success(message, title, {
      positionClass: 'toast-bottom-center',
    });
  }
  notification(message: string, title?: string) {
    this.toastrService.success(message, title, {
      positionClass: 'toast-top-right',
    });
  }
  returnPublicLink(
    type: 'galaxy' | 'star',
    id: string,
    transmitSignal: boolean
  ) {
    let link = transmitSignal ? '' : `${environment.baseUrl}space/${id}/view`;
    switch (type) {
      case 'galaxy':
        if (transmitSignal) {
          link = `${environment.webLink}/p-view/galaxy/${id}/view?src=web`;
        }
        break;

      case 'star':
        if (transmitSignal) {
          link = `${environment.webLink}/p-view/star/${id}/view?src=web`;
        }
        break;
    }
    return link;
  }

  errorHandler(error: HttpErrorResponse) {
    if (
      error &&
      error.error &&
      error.error.message &&
      typeof error.error.message == 'string'
    ) {
      this.showWarningToastr(error.error.message);
    } else {
      this.showWarningToastr(
        'Something went wrong. Please try again or later!'
      );
    }
  }

  setView(value: boolean, page: Pages) {
    this.storage.set(page, value ? 'list' : 'grid');
  }

  getView(page: Pages): boolean {
    let value = this.storage.get(page);
    if (value) {
      return value == 'list' ? true : false;
    } else {
      return false;
    }
  }

  share(linkcode: string) {
    this.showWarningToastr(
      'You can set the file accessible rights from the star permissions.'
    );
    setTimeout(() => {
      navigator
        .share({ url: this.returnPublicLink('star', linkcode, true) })
        .finally(() => {});
    }, 1000);
  }

  sortByDate = [
    { action: 'date', name: 'Last 7 days', value: '7' },
    { action: 'date', name: 'Last 30 days', value: '30' },
    { action: 'date', name: 'Last 60 days', value: '60' },
    { action: 'date', name: 'No filter', value: '' },
  ];

  blobToFile(theBlob: Blob, fileName: string): File {
    let b: any = theBlob;

    //A Blob() is almost a File() - it's just missing the two properties below which we will add
    b.lastModified = new Date();
    b.name = fileName;

    //Cast to a File() type
    return new File([b], b.name, { type: theBlob.type });
  }

  setGalaxyData(data: any) {
    if (!this.isBrowser()) {
      return;
    }

    localStorage.setItem('galaxyData', JSON.stringify(data));
    this.galaxyData.next(data);
  }

  private loadFromLocal() {
    if (!this.isBrowser()) {
      return null;
    }

    const data = localStorage.getItem('galaxyData');
    return data ? JSON.parse(data) : null;
  }

  setPgalaxyData(data:any){
    if (!this.isBrowser()) {
      return;
    }

    localStorage.setItem('pGalaxyData', JSON.stringify(data));
    this.pGalaxyData.next(data);
  }
  private pGalaxyloadFromLocal() {
    if (!this.isBrowser()) {
      return null;
    }

    const data = localStorage.getItem('pGalaxyData');

    if (!data || data === "undefined" || data === "null") {
      return null;
    }

    try {
      return JSON.parse(data);
    } catch (err) {
      console.error("Invalid JSON in pGalaxyData:", err);
      return null;
    }
  }


  clearGalaxyData() {
    if (!this.isBrowser()) {
      return;
    }

    localStorage.removeItem('galaxyData');
    this.galaxyData.next(null);
  }
  clearPGalaxyData() {
    if (!this.isBrowser()) {
      return;
    }

    localStorage.removeItem('pGalaxyData');
    this.pGalaxyData.next(null);
  }

  setStatus(value: boolean) {
    if (!this.isBrowser()) {
      return;
    }

    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(value));
    this.statusSubject.next(value);
  }

  clearStatus() {
    if (!this.isBrowser()) {
      return;
    }

    localStorage.removeItem(this.STORAGE_KEY);
    this.statusSubject.next(false);
  }

  private getStoredValue(): boolean {
    if (!this.isBrowser()) {
      return false;
    }

    const storedValue = localStorage.getItem(this.STORAGE_KEY);
    return storedValue ? JSON.parse(storedValue) : false;
  }

  setHomeFilter(sortBy: any) {
    this.storage.set('homeFilter', JSON.stringify(sortBy));
  }

  getHomeFilter() {
    const f = this.storage.get('homeFilter');
    return f ? JSON.parse(f) : '';
  }

  setGalaxyFilter(sortBy: any) {
    this.storage.set('galaxyFilter', JSON.stringify(sortBy));
  }

  getGalaxyFilter() {
    const f = this.storage.get('galaxyFilter');
    return f ? JSON.parse(f) : '';
  }

  setFavoriteFilter(sortBy: any) {
    this.storage.set('favoriteFilter', JSON.stringify(sortBy));
  }

  getFavoriteFilter() {
    const f = this.storage.get('favoriteFilter');
    return f ? JSON.parse(f) : '';
  }

  setGalaxyDetailFilter(sortBy: any) {
    this.storage.set('galaxyDetailFilter', JSON.stringify(sortBy));
  }

  getGalaxyDetailFilter() {
    const f = this.storage.get('galaxyDetailFilter');
    return f ? JSON.parse(f) : '';
  }
}

export type Pages =
  | 'frontierList'
  | 'shiningStarList'
  | 'galaxyList'
  | 'galaxyDList'
  | 'pGalaxyList';
